import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DollarSign,
  Play,
  Clock,
  Calculator,
  FileText,
  TrendingUp,
  Monitor,
  Search,
} from 'lucide-react';
import { format, subDays } from 'date-fns';

export default function UsageBillingPage() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [clients, setClients] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [playSummary, setPlaySummary] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [groupBy, setGroupBy] = useState('screen');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      loadPlaySummary();
    }
  }, [dateRange, groupBy]);

  const loadData = async () => {
    try {
      const [campaignsRes, clientsRes] = await Promise.all([
        api.get('/campaigns'),
        isAdmin ? api.get('/users?role=client') : Promise.resolve({ data: [] }),
      ]);
      setCampaigns(campaignsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadPlaySummary = async () => {
    try {
      const response = await api.get('/play-logs/summary', {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end,
          group_by: groupBy,
        },
      });
      setPlaySummary(response.data);
    } catch (error) {
      console.error('Failed to load play summary');
    }
  };

  const handlePreviewBilling = async () => {
    if (!selectedCampaign) {
      toast.error('Please select a campaign');
      return;
    }

    try {
      const response = await api.get('/billing/usage-preview', {
        params: {
          campaign_id: selectedCampaign,
          start_date: dateRange.start,
          end_date: dateRange.end,
        },
      });
      setPreviewData(response.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview billing');
    }
  };

  const handleGenerateInvoice = async () => {
    if (!selectedCampaign || !selectedClient) {
      toast.error('Please select campaign and client');
      return;
    }

    try {
      await api.post('/billing/generate-usage-invoice', {
        campaign_id: selectedCampaign,
        client_id: selectedClient,
        billing_period_start: dateRange.start,
        billing_period_end: dateRange.end,
      });
      toast.success('Usage invoice generated successfully');
      setGenerateDialogOpen(false);
      setSelectedCampaign('');
      setSelectedClient('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="usage-billing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Usage-Based Billing
          </h1>
          <p className="text-zinc-400 mt-1">
            Track plays and generate invoices based on actual usage
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setGenerateDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-500 btn-glow"
            data-testid="generate-invoice-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Generate Invoice
          </Button>
        )}
      </div>

      {/* Date Range & Filters */}
      <Card className="glass-card border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <Label className="text-zinc-400 text-xs">Start Date</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>
            <div className="flex-1">
              <Label className="text-zinc-400 text-xs">End Date</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>
            <div className="flex-1">
              <Label className="text-zinc-400 text-xs">Group By</Label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="screen">Screen</SelectItem>
                  <SelectItem value="content">Content</SelectItem>
                  <SelectItem value="campaign">Campaign</SelectItem>
                  <SelectItem value="day">Day</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Play className="w-6 h-6 text-blue-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {(playSummary?.totals?.total_plays || 0).toLocaleString()}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Total Plays</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {Math.round((playSummary?.totals?.total_duration_seconds || 0) / 60).toLocaleString()}m
              </p>
              <p className="text-sm text-zinc-400 mt-1">Total Duration</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-amber-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                ${playSummary?.data?.reduce((sum, d) => sum + (d.estimated_cost || 0), 0).toLocaleString() || 0}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Estimated Revenue</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Billing Section */}
      {isAdmin && (
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Preview Campaign Billing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Label className="text-zinc-400 text-xs">Select Campaign</Label>
                <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white" data-testid="campaign-select">
                    <SelectValue placeholder="Select campaign" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {campaigns.map((campaign) => (
                      <SelectItem key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handlePreviewBilling}
                variant="outline"
                className="border-zinc-700 self-end"
                data-testid="preview-billing-btn"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Preview Billing
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Play Summary Table */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Play Summary by {groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {(!playSummary?.data || playSummary.data.length === 0) ? (
            <div className="p-12 text-center">
              <Play className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-semibold text-white mb-2">No play data</h3>
              <p className="text-zinc-400">No plays recorded for the selected period</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500">Name</TableHead>
                  <TableHead className="text-zinc-500 text-right">Total Plays</TableHead>
                  <TableHead className="text-zinc-500 text-right">Duration (min)</TableHead>
                  {groupBy === 'screen' && (
                    <TableHead className="text-zinc-500 text-right">Est. Revenue</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {playSummary.data.map((item, index) => (
                  <TableRow key={index} className="border-zinc-800">
                    <TableCell className="text-white font-medium">{item.name}</TableCell>
                    <TableCell className="text-zinc-400 text-right">
                      {item.total_plays.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-zinc-400 text-right">
                      {item.total_duration_minutes}
                    </TableCell>
                    {groupBy === 'screen' && (
                      <TableCell className="text-emerald-400 text-right font-medium">
                        ${item.estimated_cost?.toLocaleString() || '0'}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Billing Preview: {previewData?.campaign_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-zinc-800">
                <p className="text-zinc-400 text-sm">Period</p>
                <p className="text-white font-medium">
                  {previewData?.period?.start} to {previewData?.period?.end}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-zinc-800">
                <p className="text-zinc-400 text-sm">Total Plays</p>
                <p className="text-white font-medium text-xl">
                  {previewData?.total_plays?.toLocaleString()}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-zinc-800">
                <p className="text-zinc-400 text-sm">Total Duration</p>
                <p className="text-white font-medium">
                  {previewData?.total_duration_formatted}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-emerald-400 text-sm">Total Cost</p>
                <p className="text-emerald-400 font-bold text-xl">
                  ${previewData?.total_cost?.toLocaleString()}
                </p>
              </div>
            </div>

            <div>
              <h4 className="text-white font-medium mb-2">Screen Breakdown</h4>
              <div className="max-h-48 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-700">
                      <TableHead className="text-zinc-500">Screen</TableHead>
                      <TableHead className="text-zinc-500 text-right">Plays</TableHead>
                      <TableHead className="text-zinc-500 text-right">Rate</TableHead>
                      <TableHead className="text-zinc-500 text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData?.screen_breakdown?.map((screen, index) => (
                      <TableRow key={index} className="border-zinc-700">
                        <TableCell className="text-white">{screen.screen_name}</TableCell>
                        <TableCell className="text-zinc-400 text-right">{screen.plays}</TableCell>
                        <TableCell className="text-zinc-400 text-right">
                          ${screen.rate_per_play}/play
                        </TableCell>
                        <TableCell className="text-emerald-400 text-right">
                          ${screen.cost.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)} className="border-zinc-700">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Invoice Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Generate Usage Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Campaign</Label>
              <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue placeholder="Select campaign" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {campaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-300">Client</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Billing Period Start</Label>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Billing Period End</Label>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleGenerateInvoice}
              className="bg-blue-600 hover:bg-blue-500"
              data-testid="confirm-generate-invoice-btn"
            >
              Generate Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
